import { Beaker, FlaskConical, Sparkles } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { cn } from '@reflex/ui/lib/utils';
import type { PluginReleaseStatus } from '@/lib/types';

export interface ReleaseStatusBadgeProps {
  /** Plugin maturity tier. `released` renders nothing — the unconditional case. */
  status: PluginReleaseStatus;
  /** Stable test id suffix (typically the plugin name). */
  testIdSuffix?: string;
  /** Optional class override merged via `cn()`. */
  className?: string;
}

interface StyleSpec {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Badge tint. Mirrors the channel ordering in PLUGIN_RELEASE_STATUSES. */
  classes: string;
  description: string;
}

const STYLES: Record<Exclude<PluginReleaseStatus, 'released'>, StyleSpec> = {
  beta: {
    label: 'Beta',
    icon: Beaker,
    classes: 'border-status-info/40 bg-status-info/10 text-status-info',
    description:
      'Feature-complete and broadly stable. May still have rough edges; report regressions as you find them.',
  },
  alpha: {
    label: 'Alpha',
    icon: FlaskConical,
    classes: 'border-status-warning/40 bg-status-warning/10 text-status-warning',
    description:
      'Functional but rough. APIs and UI may change; expect to file bugs and re-test as it stabilises.',
  },
  preview: {
    label: 'Preview',
    icon: Sparkles,
    classes: 'border-status-error/40 bg-status-error/10 text-status-error',
    description:
      'Earliest look. May be incomplete, broken, or change shape without notice. Use with caution.',
  },
};

/**
 * Compact maturity-tier pill rendered beside the plugin name on the per-org
 * plugins page. Renders nothing for `released` plugins — only the
 * pre-release tiers need a visual marker because they signal extra risk
 * the user should weigh before installing.
 *
 * The badge is keyboard-focusable through its tooltip trigger so screen
 * readers can read the description, and uses distinct color palettes for
 * each tier so a quick scan reveals the channel mix at a glance.
 */
export function ReleaseStatusBadge({ status, testIdSuffix, className }: ReleaseStatusBadgeProps) {
  if (status === 'released') return null;
  const spec = STYLES[status];
  const Icon = spec.icon;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          data-testid={
            testIdSuffix ? `plugin-row-${testIdSuffix}-release-status` : 'plugin-release-status'
          }
          data-release-status={status}
          className={cn('gap-1 text-2xs font-normal', spec.classes, className)}
        >
          <Icon className="h-3 w-3" />
          {spec.label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{spec.description}</TooltipContent>
    </Tooltip>
  );
}
